import React, { useState, useEffect, useRef } from 'react';
import './DocumentModal.css';
import { Icons } from '../Icons/Icons';
import t from '../../i18n/useTranslation';

function DocumentModal({ msg, currentUser, conversations, groups, allUsers = [], onClose, onForward }) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [showForward, setShowForward] = useState(false);
    const [forwardTargets, setForwardTargets] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const displayText = msg.extractedText || msg.translatedExtractedText || '';
    const [editedText, setEditedText] = useState(displayText);
    const [savedText, setSavedText] = useState(displayText);
    const utteranceRef = useRef(null);

    useEffect(() => {
    return () => { 
        if (window.speechSynthesis) window.speechSynthesis.cancel(); 
    };
}, []);

    const getFileIcon = (fileName) => {
        const ext = fileName?.split('.').pop()?.toLowerCase();
        const icons = { pdf: '📕', docx: '📘', doc: '📘', xlsx: '📗', xls: '📗', pptx: '📙', ppt: '📙', txt: '📄' };
        return icons[ext] || '📄';
    };

    const buildUtterance = async () => {
        const text = msg.extractedText || msg.text || '';
        if (!text) return null;
        const utterance = new SpeechSynthesisUtterance(text);
        const langMap = {
            'español': 'es', 'inglés': 'en', 'francés': 'fr', 'alemán': 'de',
            'portugués': 'pt', 'chino': 'zh', 'japonés': 'ja', 'coreano': 'ko',
            'árabe': 'ar', 'ruso': 'ru', 'italiano': 'it', 'euskera': 'es'
        };
        const langCode = langMap[msg.language] || 'es';
        let voices = window.speechSynthesis.getVoices();
        if (voices.length === 0) {
            await new Promise(resolve => window.speechSynthesis.addEventListener('voiceschanged', () => resolve(window.speechSynthesis.getVoices()), { once: true }));
            voices = window.speechSynthesis.getVoices();
        }
        const langVoices = voices.filter(v => v.lang.startsWith(langCode));
        const senderVoice = msg.sender?.voice || 'female';
        if (langVoices.length > 0) {
            let preferred;
            if (senderVoice === 'male') { preferred = langVoices.find(v => /\bMale\b|Pablo/i.test(v.name)) || langVoices[langVoices.length - 1]; }
            else { preferred = langVoices.find(v => /\bFemale\b|Helena|Laura/i.test(v.name)) || langVoices[0]; }
            utterance.voice = preferred;
            utterance.lang = preferred.lang;
        }
        utterance.rate = 1;
        utterance.pitch = senderVoice === 'male' ? 0.85 : 1.1;
        utterance.onend = () => { setIsPlaying(false); setIsPaused(false); };
        return utterance;
    };

    const handlePlay = async () => {
        if (!window.speechSynthesis) return;
        if (isPaused) {
            window.speechSynthesis.resume();
            setIsPaused(false);
            setIsPlaying(true);
            return;
        }
        window.speechSynthesis.cancel();
        const utterance = await buildUtterance();
        if (!utterance) return;
        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
        setIsPlaying(true);
        setIsPaused(false);
    };

    const handlePause = () => { 
    if (!window.speechSynthesis) return;
    window.speechSynthesis.pause(); 
    setIsPaused(true); 
    setIsPlaying(false); 
};

const handleStop = () => { 
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel(); 
    setIsPlaying(false); 
    setIsPaused(false); 
};

    const handleDownload = () => {
        const a = document.createElement('a');
        a.href = msg.mediaUrl;
        a.download = msg.fileName || msg.originalText || 'documento';
        a.target = '_blank';
        a.click();
    };

    const handleToggleForwardTarget = (id) => {
        setForwardTargets(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleSendForward = () => {
        if (forwardTargets.length === 0) return;
        // Usar el texto editado más reciente: si está editando, usa editedText
        const finalText = isEditing ? editedText : savedText;
        onForward({ ...msg, extractedText: finalText }, forwardTargets);
        setShowForward(false);
        setForwardTargets([]);
        onClose();
    };

    const fileName = msg.fileName || msg.originalText || 'Documento';
    const text = savedText;

    return (
        <div className="doc-modal-overlay" onClick={onClose}>
            <div className="doc-modal" onClick={e => e.stopPropagation()}>
                <div className="doc-modal-header">
                    <div className="doc-modal-title">
                        <span className="doc-modal-icon">{getFileIcon(fileName)}</span>
                        <span className="doc-modal-name">{fileName}</span>
                    </div>
                    <div className="doc-modal-actions">
                        {text && (
                            <>
                                {!isPlaying && !isPaused && (
                                    <button className="doc-action-btn doc-play" onClick={handlePlay}>
                                        <Icons.Play size={14} color="white" />
                                    </button>
                                )}
                                {isPlaying && (
                                    <button className="doc-action-btn doc-pause" onClick={handlePause}>
                                        <Icons.Pause size={14} color="white" />
                                    </button>
                                )}
                                {isPaused && (
                                    <button className="doc-action-btn doc-play" onClick={handlePlay}>
                                        <Icons.Play size={14} color="white" />
                                    </button>
                                )}
                                {(isPlaying || isPaused) && (
                                    <button className="doc-action-btn doc-stop" onClick={handleStop}>
                                        <Icons.Stop size={14} color="white" />
                                    </button>
                                )}
                            </>
                        )}
                        <button className="doc-action-btn doc-download" onClick={handleDownload}>
                            <Icons.Download size={16} color="white" />
                        </button>
                        {!isEditing ? (
                            <button className="doc-action-btn" onClick={() => { setIsEditing(true); handleStop(); }}>
                                <Icons.Edit size={16} color="white" />
                            </button>
                        ) : (
                            <>
                                <button className="doc-action-btn" onClick={() => { setSavedText(editedText); setIsEditing(false); }} style={{ background: 'rgba(0,180,100,0.6)' }}>
                                    <Icons.Save size={16} color="white" />
                                </button>
                                <button className="doc-action-btn" onClick={() => { setEditedText(savedText); setIsEditing(false); }}>
                                    <Icons.Close size={16} color="white" />
                                </button>
                            </>
                        )}
                        <button className="doc-action-btn doc-send" onClick={() => {
                            // Auto-guardar si está editando antes de abrir reenvío
                            if (isEditing) { setSavedText(editedText); setIsEditing(false); }
                            setShowForward(true);
                        }}>
                            <Icons.Share size={16} color="white" />
                        </button>
                        <button className="doc-action-btn doc-close" onClick={onClose}>
                            <Icons.Close size={16} color="white" />
                        </button>
                    </div>
                </div>

                <div className="doc-modal-body">
                    {isEditing ? (
                        <textarea className="doc-modal-edit" value={editedText} onChange={e => setEditedText(e.target.value)} autoFocus />
                    ) : text ? (
                        <pre className="doc-modal-text">{text}</pre>
                    ) : (
                        <div className="doc-modal-empty">
                            <p>{t.doc.noPreview}</p>
                            <p>{t.doc.downloadMsg}</p>
                        </div>
                    )}
                </div>

                {showForward && (
                    <div className="doc-forward-overlay" onClick={() => setShowForward(false)}>
                        <div className="doc-forward-modal" onClick={e => e.stopPropagation()}>
                            <h3>{t.doc.sendTo}</h3>
                            <div className="doc-forward-list">
                                {conversations.map(conv => (
                                    <div key={conv.id} className={`doc-forward-item ${forwardTargets.includes(conv.id) ? 'selected' : ''}`} onClick={() => handleToggleForwardTarget(conv.id)}>
                                        <div className="doc-forward-avatar">{conv.otherUser.name.charAt(0)}</div>
                                        <div className="doc-forward-name">{conv.otherUser.name}</div>
                                        <div className={`doc-forward-check ${forwardTargets.includes(conv.id) ? 'checked' : ''}`}>
                                            {forwardTargets.includes(conv.id) && <Icons.Check size={12} color="white" />}
                                        </div>
                                    </div>
                                ))}
                                {groups.map(group => (
                                    <div key={group.id} className={`doc-forward-item ${forwardTargets.includes(group.id) ? 'selected' : ''}`} onClick={() => handleToggleForwardTarget(group.id)}>
                                        <div className="doc-forward-avatar"><Icons.Group size={16} color="white" /></div>
                                        <div className="doc-forward-name">{group.name}</div>
                                        <div className={`doc-forward-check ${forwardTargets.includes(group.id) ? 'checked' : ''}`}>
                                            {forwardTargets.includes(group.id) && <Icons.Check size={12} color="white" />}
                                        </div>
                                    </div>
                                ))}
                                {allUsers.filter(u => !conversations.some(c => c.otherUser?.id === u._id)).map(user => (
                                    <div key={user._id} className={`doc-forward-item ${forwardTargets.includes(user._id) ? 'selected' : ''}`} onClick={() => handleToggleForwardTarget(user._id)}>
                                        <div className="doc-forward-avatar">{user.name.charAt(0)}</div>
                                        <div className="doc-forward-name">{user.name} <span style={{fontSize:'11px',color:'#999'}}>({user.language})</span></div>
                                        <div className={`doc-forward-check ${forwardTargets.includes(user._id) ? 'checked' : ''}`}>
                                            {forwardTargets.includes(user._id) && <Icons.Check size={12} color="white" />}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button className="doc-forward-btn" disabled={forwardTargets.length === 0} onClick={handleSendForward}>
                                {t.doc.send} {forwardTargets.length > 0 ? `(${forwardTargets.length})` : ''}
                            </button>
                            <button className="doc-forward-cancel" onClick={() => setShowForward(false)}>{t.doc.cancel}</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default DocumentModal;